// Offers: what Pocket Nexus gives away outside of payment, such as a
// membership for life to the people who sponsored its founder on GitHub, or
// a month to the winners of a draw. An editor makes an offer and its list
// with the command line; an account claims it at /studio/?offer=<id>, a page
// no part of the Studio links to. The accounts database holds the offers,
// their lists and the memberships they gave (server/account/schema.ts).
//
// An entry of a list is `github:<GitHub user id>`, which an account proves by
// signing in with that GitHub account or linking it, or `code:<SHA-256>` of a
// code an editor handed out. A code is never stored, only its hash.

/** An offer's id: what its address carries. Lowercase letters, digits and inner hyphens, 3 to 48 of them. */
export const OFFER_ID = /^[a-z0-9](?:[a-z0-9-]{1,46})[a-z0-9]$/;

/** What an offer is, as an account sees it. `days` null is for life. */
export interface OfferInfo {
  id: string;
  title: string;
  proof: "github" | "code";
  days: number | null;
  closesAt: number | null;
}

/**
 * Where an account stands with an offer:
 * - `sign-in`: no account is signed in (a guest is none);
 * - `granted`: the account holds the offer's membership;
 * - `ready`: it can claim it now (for a code offer: once it types the code);
 * - `connect-github`: the offer is for GitHub accounts and the account has none linked;
 * - `not-listed`: its GitHub account is not on the list;
 * - `used`: the GitHub account or the code was claimed by another account;
 * - `wrong-code`: no such code on the list;
 * - `closed`: the offer takes no more claims.
 */
export type OfferStanding = "sign-in" | "granted" | "ready" | "connect-github" | "not-listed" | "used" | "wrong-code" | "closed";

/** GET and POST /api/offers/:id. `claimed` is true on the answer to the POST that made the grant; `until` is the granted membership's end, null for life. */
export interface OfferState {
  offer: OfferInfo;
  state: OfferStanding;
  claimed?: boolean;
  until?: number | null;
}

/** POST /api/offers/:id: the code, for a code offer. */
export interface OfferClaimRequest {
  code?: string;
}

/** A code as an account types it: the letters and digits, in capitals. Spaces, dashes and case do not matter. */
export const normalCode = (code: string): string => code.toUpperCase().replace(/[^0-9A-Z]/g, "");

export const githubKey = (githubId: string | number): string => `github:${githubId}`;

/** The list key of a code: its SHA-256, so the list never holds a code. */
export async function codeKey(code: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(normalCode(code)));
  return `code:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

/** Letters and digits a code is made of: no 0, 1, I, L, O, S, U, V, so none is read as another. */
export const CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRTWXYZ";
/** Characters in a code: 15 from 28 is about 72 bits, three groups of five as it is shown. */
export const CODE_LENGTH = 15;

/** One new code from `random` bytes, shown in groups of five. */
export function makeCode(random: (size: number) => Uint8Array): string {
  const chars: string[] = [];
  // Rejection sampling keeps each character equally likely.
  const limit = 256 - (256 % CODE_ALPHABET.length);
  while (chars.length < CODE_LENGTH) {
    for (const byte of random(CODE_LENGTH * 2)) {
      if (byte < limit && chars.length < CODE_LENGTH) chars.push(CODE_ALPHABET[byte % CODE_ALPHABET.length]!);
    }
  }
  return [0, 5, 10].map((at) => chars.slice(at, at + 5).join("")).join("-");
}

/** What an editor sets with PUT /api/cli/offers/:id. `days` null gives a membership for life; `closesAt` null keeps it open. */
export interface OfferRequest {
  title: string;
  proof: "github" | "code";
  days: number | null;
  closesAt: number | null;
}

/** POST /api/cli/offers/:id/entries: GitHub user ids to add or remove, or a number of new codes to make. */
export interface OfferEntriesRequest {
  github?: Array<string | number>;
  remove?: Array<string | number>;
  codes?: number;
}

/** What the editor's routes answer: the offer, its list's size, and who claimed it. New codes are answered once and kept nowhere. */
export interface OfferReport {
  offer: OfferInfo;
  entries: number;
  claimed: Array<{ key: string; by: string; handle: string; at: number }>;
  codes?: string[];
  added?: number;
  removed?: number;
}
